import type {
  AvanceDeclarationDto,
  AvanceDeclarationRowDto,
  AvanceRowDto,
  AvanceSnapshotViewDto,
  AvanceStepAdvanceDto,
  AvanceStepAdvanceViewDto,
  CertificationStageViewDto,
  CertificationStepDto,
  SiiAvanceViewDto,
} from '@/services/generated/api';

/**
 * The «Avance SII» wire types, as the feature names them.
 *
 * Everything here is an alias of the client `npm run api:codegen` generates
 * from `openapi.json` (the `SiiAvance` tag of `sii-avance.controller.ts`), so
 * the contract has one source: the backend DTOs. The unions the generated
 * types only carry inline (reason codes, error codes, stages) are pulled out
 * by indexed access, which keeps the `Record`s in `copy.ts` exhaustive against
 * the spec rather than against a hand-kept list that could drift.
 *
 * The hooks are not re-exported: consumers import
 * `useSiiAvanceControllerReadQuery` and friends from
 * `@/services/generated/api` directly, and the `SiiAvance` tag those
 * endpoints provide and invalidate is the one realtime frames invalidate.
 */

/** One row of the «Avance SII» table (spec §2, §4). */
export type AvanceRow = AvanceRowDto;

export type AvanceRowKind = AvanceRowDto['kind'];

export type AvanceReasonCode = AvanceRowDto['reasons'][number];

export type AvanceSourceType = NonNullable<AvanceRowDto['sourceType']>;

export type AvanceEstadoClass = NonNullable<AvanceRowDto['siiEstadoClass']>;

export type AvanceDeclarationRow = AvanceDeclarationRowDto;

export type AvanceDeclaration = AvanceDeclarationDto;

export type AvanceDeclarationStatus = AvanceDeclarationDto['status'];

export type AvanceErrorCode = NonNullable<AvanceDeclarationDto['errorCode']>;

export type CertificationStage = CertificationStageViewDto['current'];

export type CertificationStageSource = CertificationStageViewDto['source'];

export type CertificationStepStatus = CertificationStepDto['status'];

export type CertificationStageView = CertificationStageViewDto;

export type AvanceSnapshotView = AvanceSnapshotViewDto;

/** One press of «Avanzar Siguiente Paso». */
export type AvanceStepAdvance = AvanceStepAdvanceDto;

export type AvanceStepAdvanceView = AvanceStepAdvanceViewDto;

/** `GET /api/companies/:companyId/sii-avance` (spec §4). */
export type SiiAvanceView = SiiAvanceViewDto;

/** The six steps in the official order (spec §0 F1). */
export const CERTIFICATION_STAGES: readonly CertificationStage[] = [
  'set_de_pruebas',
  'simulacion',
  'intercambio',
  'muestras_impresas',
  'declaracion_cumplimiento',
  'autorizado',
];
